/**
 * ShopProductView — Product detail page for the public online shop
 * URL: /shop/:businessId/product/:productId
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';
import { getProductImages, type ShopProduct } from '../../utils/shopProduct';
import ProductImageGallery from '../../components/shop/ProductImageGallery';
import {
  isWholesaleShopCustomer,
  readShopCustomer,
  readShopToken,
  syncShopSession,
  type ShopCustomer,
} from '../../utils/shopSession';
import './PublicShop.css';

interface CartItem {
  product: ShopProduct;
  qty: number;
  saleType: 'unit' | 'box';
}

const ShopProductView: React.FC = () => {
  const { businessId, productId } = useParams<{ businessId: string; productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ShopProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [shopCustomer, setShopCustomer] = useState<ShopCustomer | null>(() => (businessId ? readShopCustomer(businessId) : null));
  const isWholesaleCustomer = isWholesaleShopCustomer(shopCustomer?.customer_type);

  useEffect(() => {
    if (!businessId || !productId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const session = await syncShopSession(businessId);
        const token = session?.token ?? readShopToken(businessId);
        if (!cancelled && session?.customer) setShopCustomer(session.customer);

        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/products/public/${businessId}`, { headers });
        const data = await res.json();
        const list: ShopProduct[] = Array.isArray(data.products) ? data.products : [];
        if (!cancelled) {
          setProduct(list.find(p => String(p.id) === String(productId)) || null);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [businessId, productId]);

  const addToCart = (saleType: 'unit' | 'box') => {
    if (!product || !businessId) return;
    try {
      const saved = localStorage.getItem(`cart_${businessId}`);
      const cart: CartItem[] = saved ? JSON.parse(saved) : [];
      const existing = cart.find(c => c.product.id === product.id && c.saleType === saleType);
      const next = existing
        ? cart.map(c => c.product.id === product.id && c.saleType === saleType ? { ...c, qty: c.qty + 1 } : c)
        : [...cart, { product, qty: 1, saleType }];
      localStorage.setItem(`cart_${businessId}`, JSON.stringify(next));
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="shop-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#6b7280' }}>Loading…</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="shop-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 12 }}>
        <p style={{ color: '#6b7280' }}>Product not found.</p>
        <Link to={`/shop/${businessId}`} style={{ color: '#2563eb', fontWeight: 600 }}>← Back to shop</Link>
      </div>
    );
  }

  const images = getProductImages(product);
  const outOfStock = product.stock === 0;
  const hasBox = product.box_quantity != null && Number(product.box_quantity) > 0;
  const unitPrice = isWholesaleCustomer && product.wholesale_price && Number(product.wholesale_price) > 0
    ? Number(product.wholesale_price)
    : Number(product.price);
  const boxPrice = hasBox
    ? (isWholesaleCustomer && Number(product.wholesale_price) > 0
      ? Number(product.wholesale_price) * Number(product.box_quantity)
      : Number(product.price) * Number(product.box_quantity))
    : 0;

  return (
    <div className="shop-page">
      <header className="shop-banner">
        <div className="shop-banner-inner">
          <div className="shop-banner-top">
            <button type="button" className="shop-btn" onClick={() => navigate(`/shop/${businessId}`)}>← Shop</button>
            <button type="button" className="shop-btn shop-btn--cart" onClick={() => navigate(`/shop/${businessId}`)}>
              🛒 View cart
            </button>
          </div>
        </div>
      </header>

      <main className="shop-main">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, background: '#fff', borderRadius: 12, border: '1px solid #e8ecf2', padding: 20 }}>
          <ProductImageGallery images={images} alt={product.name} variant="detail" />

          <div>
            {product.category && <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>{product.category}</span>}
            <h1 style={{ margin: '6px 0 8px', fontSize: 24, fontWeight: 800, color: '#111827' }}>{product.name}</h1>
            {product.sku && <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af' }}>SKU {product.sku}</p>}
            {product.expiry_date && (
              <p style={{ margin: '0 0 8px', fontSize: 13, color: '#b45309', fontWeight: 600 }}>
                Best before: {new Date(product.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            )}
            {product.description && (
              <p style={{ margin: '0 0 16px', fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}>{product.description}</p>
            )}

            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: '#1d4ed8' }}>£{unitPrice.toFixed(2)}</span>
              <span style={{ fontSize: 14, color: '#9ca3af' }}> / {product.unit || 'unit'}</span>
              {isWholesaleCustomer && product.wholesale_price && Number(product.wholesale_price) > 0 && (
                <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>Trade price</span>
              )}
              {hasBox && (
                <p style={{ margin: '8px 0 0', fontSize: 14, color: '#7c3aed', fontWeight: 600 }}>
                  Box of {product.box_quantity}: £{boxPrice.toFixed(2)}
                </p>
              )}
            </div>

            {outOfStock ? (
              <p style={{ color: '#ef4444', fontWeight: 600 }}>Out of stock</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
                <button type="button" className="shop-add" onClick={() => addToCart('unit')}>
                  + Add to cart — £{unitPrice.toFixed(2)}
                </button>
                {hasBox && (
                  <button type="button" className="shop-add shop-add--box" onClick={() => addToCart('box')}>
                    📦 Add box — £{boxPrice.toFixed(2)}
                  </button>
                )}
                {added && <p style={{ margin: 0, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>✓ Added — open cart from the shop page</p>}
              </div>
            )}

            {product.stock > 0 && product.stock < 20 && !outOfStock && (
              <p style={{ marginTop: 12, fontSize: 13, color: '#f97316' }}>{product.stock} in stock</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShopProductView;
